import { Inject, inject, Injectable, LOCALE_ID, Optional, RendererFactory2, DOCUMENT } from '@angular/core'
import { RuntimeService } from 'src/app/service/runtime.service'
import { Environment } from 'src/environments/environment'
import { CookieKey, ED_INJECT_EXPRESS_REQUEST, ED_INJECT_LOCALE_ID, PageLocales } from 'src/server/server.data'

import { MetaService } from 'src/app/service/meta/meta.service'
import { CookieInterface } from 'src/tools/cookie.tools'
import { DomService } from 'src/app/service/dom.service'
import { Request } from 'express'
import { PageContext, Paynow4SaleType, RedirectorService } from 'src/app/service/redirector.service'
import { Meta } from '@angular/platform-browser'
import { ActivatedRoute } from '@angular/router'
import { takeUntil } from 'rxjs'
import { OfferExpiredNoteComponent } from 'src/app/elements/offer-expired-note/offer-expired-note.component'
import { MatDialog } from '@angular/material/dialog'
import { PriceSuggestionComponent } from 'src/app/elements/price-suggestion/price-suggestion.component'

declare const window: any

@Injectable()
export class PageService {
  protected prev?: string

  constructor (
    protected runtimeService: RuntimeService,
    protected redirectorService: RedirectorService,
    protected domService: DomService,
    protected rendererFactory2: RendererFactory2,
    protected metaService: MetaService,
    protected meta: Meta,
    protected route: ActivatedRoute,
    protected dialog: MatDialog,
    @Inject(DOCUMENT) protected document: Document,
    @Inject(ED_INJECT_LOCALE_ID) protected localeId: string,
    @Optional() @Inject(ED_INJECT_EXPRESS_REQUEST) protected request: Request,
  ) {
    this.plausibleServer()
    this.languageSuggest()
    this.crisp()
    this.theme()
    this.colorSelector()
    this.offerExpired()
    this.priceOffer()
  }

  protected crisp () {
    if (this.runtimeService.isServer() || !Environment.integrations.crisp) {
      return;
    }

    import('src/app/elements/crisp/crisp.component').then(m => m.CrispComponent)
      .then(component => this.domService.inject(component))
  }

  languageSuggest () {
    // user has previously made a selection
    if (this.runtimeService.isServer() || CookieInterface.get(CookieKey.I18nSelection)) {
      return
    }

    const appLocale = inject(LOCALE_ID)
    const userLocale = navigator.language?.split('-')[0]
    if (
      userLocale
      && appLocale !== userLocale
      && PageLocales.map(l => l.key).includes(userLocale)
    ) {
      import('src/app/elements/i18n-suggest/i18n-suggest.component').then(m => m.I18nSuggestComponent)
        .then(component => this.domService.inject(component))
    }
  }

  offerExpired () {
    // user has previously made a selection
    if (this.runtimeService.isServer()) {
      return
    }

    this.route.queryParams
      .subscribe(params => {
        if (params['offer_expired']) {
          this.dialog.open(OfferExpiredNoteComponent, {
            maxHeight: '100vh',
            maxWidth: 'calc(100vw - 30px)',
            data: this.redirectorService.getContext(),
          })
        }
      })
  }

  priceOffer () {
    // user has previously made a selection
    if (this.runtimeService.isServer()) {
      return
    }

    this.route.queryParams
      .subscribe(params => {
        if (params['suggest_price']) {
          this.dialog.open(PriceSuggestionComponent, {
            maxHeight: '100vh',
            maxWidth: 'calc(100vw - 30px)',
            data: {
              email: params['suggest_price_email'],
              price: params['suggest_price_price'],
              context: this.redirectorService.getContext(),
            },
          })
        }
      })
  }

  protected plausibleServer () {
    if (
      !Environment.integrations.plausible
      || !this.runtimeService.isServer()
    ) { return }

    const renderer2 = this.rendererFactory2.createRenderer(null, null)

    const script = renderer2.createElement('script')
    renderer2.setAttribute(script, 'defer', 'true')
    renderer2.setAttribute(script, 'data-domain', Environment.integrations.plausible.domain)
    renderer2.setAttribute(script, 'src', Environment.integrations.plausible.url + '/js/script.manual.js')
    renderer2.appendChild(inject(DOCUMENT).body, script)
  }

  protected plausibleClient () {
    if (
      !Environment.integrations.plausible
      || !this.runtimeService.isClient()
    ) { return }

    window.plausible = window.plausible || function() {
      (window.plausible.q = window.plausible.q || []).push(arguments)
    }

    const context = this.redirectorService.getContext()
    const namespace = (
      context.config.type === 'landing'
      && context.config.template === 'recently-caught'
    ) ? 'catcher' : 'buy'

    const data = {
      u: `${location.protocol}//elitedomains.de/${namespace}/${this.localeId}/${this.metaService.getDomain()}`,
    }
    window.plausible('pageview', data)
  }

  protected colorSelector () {
    if (this.runtimeService.isClient() && [
      'buyme.de',
      'preview-security.de',
      'stage-preview-security.de',
      'preview-marketing.de',
      'stage-preview-marketing.de',
      'preview-smart.de',
      'stage-preview-smart.de',
      'preview-expert.de',
      'stage-preview-expert.de',
      'preview-default.de',
      'stage-preview-default.de',
      'preview-slim.de',
      'stage-preview-slim.de',
    ].includes(inject(MetaService).getDomain())) {
      import('src/app/elements/color-selector/color-selector.component').then(m => m.ColorSelectorComponent)
        .then(component => this.domService.inject(component))
    }
  }

  protected theme () {
    if (this.runtimeService.isClient()) {
      const set = (match: MediaQueryList) => {
        if (this.prev) {
          document.body.classList.remove(this.prev)
        }

        this.prev = `e--g-theme-${match.matches ? 'dark' : 'light'}`
        document.body.classList.add(this.prev)
      }

      // const match = window.matchMedia('(prefers-color-scheme: dark)')
      // set(match)
      // match.addEventListener('change', () => set(match))
    }
  }

  public boot () {
    this.hreflang()
    this.metatags()
    this.plausibleClient()
  }

  protected hreflang () {
    if (this.runtimeService.isClient()) {
      return
    }

    const context = this.redirectorService.getContext()
    const renderer = this.rendererFactory2.createRenderer(null, null)
    const mainLocaleId = context.i18n.primary

    // Path only. An alternate has to point at the canonical URL, so emitting
    // hreflang links that carry whatever campaign parameters the visitor
    // arrived with was wrong on its own terms — and it was the only reason the
    // rendered output depended on the query string at all, which kept every
    // ?utm_source= / ?gclid= visit out of the shared page cache (ED-285).
    const path = this.request.originalUrl.split(/[?#]/)[0].slice(4)

    for (const localeId of ['de', 'en', 'es', 'fr', 'zh']) {
      const url = [
        this.request.protocol + ':/',
        this.request.hostname,
        localeId,
        path,
      ].join('/')

      const link = renderer.createElement('link')
      renderer.setProperty(link, 'rel', 'alternate')
      renderer.setProperty(link, 'hreflang', localeId)
      renderer.setProperty(link, 'href', url)
      renderer.appendChild(this.document.head, link)

      if (localeId === mainLocaleId) {
        const link = renderer.createElement('link')
        renderer.setProperty(link, 'rel', 'alternate')
        renderer.setProperty(link, 'hreflang', 'x-default')
        renderer.setProperty(link, 'href', url)
        renderer.appendChild(this.document.head, link)
      }
    }
  }

  protected metatags () {
    const context = this.redirectorService.getContext() as PageContext<Paynow4SaleType>
    if (!context.offer?.meta_tags) {
      return
    }

    for (const [name, content] of Object.entries(context.offer.meta_tags)) {
      this.meta.updateTag({ name, content })
    }
  }
}
